import { useState } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import CheckmarkIcon from "@/assets/images/icons2/icon--checkmark.svg?component";
import CloseIcon from "@/assets/images/icons2/icon--close.svg?component";
import { CosignatureDocumentTypeEnum, CosignatureStatusEnum } from "@/core/constants";
import { CosignNoteModal } from "@/features/cosignature/components/CosignNoteModal";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import type { NoteDetail } from "@/core/types";
import { RejectNoteModal } from "@/features/cosignature/components/RejectNoteModal";
import { useCosignatureMeta } from "@/features/cosignature/api/queries/useCosignatureMeta";
import { useCosignSupervisor } from "@/features/cosignature/hooks/useCosignSupervisor";

type CosignatureReviewSectionProps = {
  note: NoteDetail;
};

export function CosignatureReviewSection({ note }: CosignatureReviewSectionProps) {
  const { id: noteId, patient_id: patientId, provider_name: providerName = "" } = note;
  const { isSupervisor } = useCosignSupervisor();
  const { data: cosignatureMeta } = useCosignatureMeta({ enabled: isSupervisor, noteId });

  const [isCosignModalOpen, setIsCosignModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  // hide section immediately on success without waiting for cache invalidation to resolve
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isNoteActionable =
    cosignatureMeta?.cosignature_status === CosignatureStatusEnum.PENDING ||
    cosignatureMeta?.cosignature_status === CosignatureStatusEnum.RESUBMITTED;

  if (!isSupervisor || !isNoteActionable || isSubmitted) return null;

  return (
    <>
      <DashboardCard2
        className="tw:bg-transparent tw:shadow-none"
        classNameChildren="tw:flex tw:flex-col tw:items-center tw:gap-3"
      >
        <p className="body-md tw:text-center">
          Review the clinical documentation above and approve to co-sign, or reject to return to the
          author.
        </p>

        <div className="tw:flex tw:justify-center tw:gap-4">
          <Button2
            icon={<CloseIcon />}
            onPress={() => setIsRejectModalOpen(true)}
            variant="destructive-primary"
          >
            Reject Note
          </Button2>

          <Button2
            icon={<CheckmarkIcon />}
            onPress={() => setIsCosignModalOpen(true)}
            variant="primary"
          >
            Approve &amp; Co-Sign
          </Button2>
        </div>
      </DashboardCard2>

      <CosignNoteModal
        documentId={noteId}
        documentType={CosignatureDocumentTypeEnum.note}
        isOpen={isCosignModalOpen}
        onClose={() => setIsCosignModalOpen(false)}
        onSuccess={() => setIsSubmitted(true)}
        patientId={patientId}
        providerName={providerName}
      />

      <RejectNoteModal
        documentId={noteId}
        documentType={CosignatureDocumentTypeEnum.note}
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onSuccess={() => setIsSubmitted(true)}
        patientId={patientId}
        providerName={providerName}
      />
    </>
  );
}
